import { useQuery } from '@tanstack/react-query';
import type { PostAccountPlatforms } from '../../../types/publishing';
import {
  resolveCaptionWildcards,
  type WildcardContext,
} from '../../../lib/publishing/captionWildcards';
import { type ValidateIssue } from '../../../lib/publishing/validateWarnings';
import type { ComposerState } from '../composerState';
import { effectiveCaption, effectiveMedia, enabledTargets } from '../composerState';
import { PostPreviewList, type PostPreviewItem } from '../../../components/social/previews/PostPreviewList';
import { facebookLinkCardUrl } from '../../../lib/publishing/linkCard';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { getLinkPreview } from '../api';

// Publishing — the live preview column. One card per ticked DESTINATION (so a
// Facebook feed post and a Facebook Story render as two separate previews),
// each with that destination's EFFECTIVE caption + media, plus the per-network
// validate warnings (our platform rules + Zernio's).
//
// ⚠️ Warnings are matched by PLATFORM, not destination — Zernio's validate
// response names only the platform. With two destinations on one platform the
// same warning therefore shows on both cards. That is the honest rendering: we
// genuinely don't know which placement it refers to, and hiding it from one
// would be a guess.

export function PostPreviews({
  state,
  platforms,
  issues,
  wildcard,
  pageName,
}: {
  state: ComposerState;
  platforms: PostAccountPlatforms | null;
  // Overrides the page/handle shown on the mock (Post to Many's per-dealer
  // Preview has no platforms row, only the dealer's name).
  pageName?: string;
  issues: ValidateIssue[];
  // The selected account's caption-wildcard context. When present, the preview
  // resolves {tokens} to real values (empty fields → blank), showing what will
  // actually publish. When null (not loaded / no account), the authored caption
  // is shown with tokens intact rather than blanking them out.
  wildcard: WildcardContext | null;
}) {
  const targets = enabledTargets(state);
  const name = pageName || platforms?.profileName || 'Your page';

  // The Facebook link-card preview (2026-09-30). Which URL, if any, is the pure
  // facebookLinkCardUrl (first link in the RESOLVED caption, feed post, no
  // media). ⚠️ DEBOUNCED and cached by URL: the composer re-renders on every
  // keystroke, and typing a URL character by character would otherwise fetch
  // "https://m", "https://ma", … The query only runs once the URL has settled.
  const fbTarget = targets.find((t) => t.destination === 'facebook');
  const fbCaption = fbTarget
    ? wildcard
      ? resolveCaptionWildcards(effectiveCaption(state, 'facebook'), wildcard)
      : effectiveCaption(state, 'facebook')
    : '';
  const cardUrl = fbTarget
    ? facebookLinkCardUrl({
        caption: fbCaption,
        mediaCount: effectiveMedia(state, 'facebook').length,
        contentType: fbTarget.fbContentType === 'reel' ? 'reel' : 'feed',
      })
    : null;
  const settledUrl = useDebouncedValue(cardUrl, 800);
  const linkPreview = useQuery({
    queryKey: ['publishing-link-preview', settledUrl],
    queryFn: () => getLinkPreview(settledUrl!),
    enabled: settledUrl !== null && settledUrl === cardUrl,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  // Only a card for the URL the caption holds NOW; a failed fetch shows nothing.
  const fbLinkPreview =
    cardUrl !== null && settledUrl === cardUrl ? (linkPreview.data ?? null) : null;

  const items: PostPreviewItem[] = targets.map((t) => {
    const authored = effectiveCaption(state, t.destination);
    // Resolve wildcards for the selected account so the preview shows real
    // values; keep the authored tokens visible until the context loads.
    return {
      destination: t.destination,
      content: wildcard ? resolveCaptionWildcards(authored, wildcard) : authored,
      media: effectiveMedia(state, t.destination),
      customised:
        (t.overrideCaption && t.customContent.trim() !== '') ||
        (t.overrideMedia && t.customMedia.length > 0),
      fbContentType: t.fbContentType,
      igContentType: t.igContentType,
      gbp: t.gbp,
      firstComment: t.addFirstComment ? t.firstComment : '',
    };
  });

  return (
    <PostPreviewList items={items} pageName={name} issues={issues} facebookLinkPreview={fbLinkPreview} />
  );
}
